import { PagedListResponse, SuccessResponse, User, X5LessonSnapshot } from '../../../domain';
import { HttpPromiseState } from '../../../lib/helpers';
import { environment } from '../../../lib/environment';
import { httpClient } from '../../../domain/utils/httpClient';
import {
    Classroom,
    ClassroomSearchDto,
    ClassroomUpdateModel,
    ClassroomUserSearchDto
} from '../models/classroom-models';
import { TeacherScheduleClassroomResponse, StudentScheduleClassroomResponse } from 'domain/models/response-model';

class StudentClassroomLoaders {
    getPendingClassrooms(): HttpPromiseState<readonly Classroom[]> {
        let url = `${environment.apiServer}/student/classroom/pending`;
        return httpClient.state.get<readonly Classroom[]>(url);
    }

    getClassroom(classroomId: number): HttpPromiseState<Classroom> {
        let url = `${environment.apiServer}/student/classroom/${classroomId}`;
        return httpClient.state.get<Classroom>(url);
    }

    getLessonSnapshot(classroomId: number): HttpPromiseState<X5LessonSnapshot> {
        let url = `${environment.apiServer}/student/classroom/${classroomId}/lesson`;
        return httpClient.state.get<X5LessonSnapshot>(url);
    }

    getClassroomSchedule(studentId: number | undefined = undefined): HttpPromiseState<StudentScheduleClassroomResponse> {
        let url = `${environment.apiServer}/student/classroom-schedule`;
        return httpClient.state.get<StudentScheduleClassroomResponse>(url, { params: { studentId } });
    }
}

class TeacherClassroomLoaders {
    getPendingClassrooms(): HttpPromiseState<readonly Classroom[]> {
        let url = `${environment.apiServer}/teacher/classroom/pending`;
        return httpClient.state.get<readonly Classroom[]>(url);
    }

    getClassroom(classroomId: number): HttpPromiseState<Classroom> {
        let url = `${environment.apiServer}/teacher/classroom/${classroomId}`;
        return httpClient.state.get<Classroom>(url);
    }

    getLessonSnapshot(classroomId: number): HttpPromiseState<X5LessonSnapshot> {
        let url = `${environment.apiServer}/teacher/classroom/${classroomId}/lesson`;
        return httpClient.state.get<X5LessonSnapshot>(url);
    }

    getClassroomSchedule(teacherId: number | undefined = undefined): HttpPromiseState<TeacherScheduleClassroomResponse> {
        let url = `${environment.apiServer}/teacher/classroom-schedule`;
        return httpClient.state.get<TeacherScheduleClassroomResponse>(url, { params: { teacherId } });
    }

    getTeacherStudents(): HttpPromiseState<User[]> {
        let url = `${environment.apiServer}/teacher/students`;
        return httpClient.state.get<User[]>(url);
    }
}

class AdminClassroomLoaders {
    searchClassroom(dto: ClassroomSearchDto): HttpPromiseState<PagedListResponse<Classroom>> {
        let url = `${environment.apiServer}/administrator/classroom/search-classroom`;
        return httpClient.state.post<PagedListResponse<Classroom>>(url, dto);
    }

    getChangeTimeSuggestions(userId: number | undefined = undefined): HttpPromiseState<PagedListResponse<Classroom>> {
        let url = `${environment.apiServer}/administrator/classroom-suggested`;
        return httpClient.state.get<PagedListResponse<Classroom>>(url, { params: { userId } });
    }

    searchStudent(dto: ClassroomUserSearchDto): HttpPromiseState<PagedListResponse<User>> {
        let url = `${environment.apiServer}/administrator/classroom/search-student`;
        return httpClient.state.post<PagedListResponse<User>>(url, dto);
    }

    searchTeacher(dto: ClassroomUserSearchDto): HttpPromiseState<PagedListResponse<User>> {
        let url = `${environment.apiServer}/administrator/classroom/search-teacher`;
        return httpClient.state.post<PagedListResponse<User>>(url, dto);
    }

    getClassroom(classroomId: number): HttpPromiseState<Classroom> {
        let url = `${environment.apiServer}/administrator/classroom/${classroomId}`;
        return httpClient.state.get<Classroom>(url);
    }

    createClassroom(dto: ClassroomUpdateModel): HttpPromiseState<SuccessResponse> {
        let url = `${environment.apiServer}/administrator/classroom/create`;
        return httpClient.state.post<SuccessResponse>(url, dto);
    }

    updateClassroom(classroomId: number, dto: ClassroomUpdateModel): HttpPromiseState<SuccessResponse> {
        let url = `${environment.apiServer}/administrator/classroom/${classroomId}`;
        return httpClient.state.put<SuccessResponse>(url, dto);
    }

    deleteClassroom(classroomId: number): HttpPromiseState<SuccessResponse> {
        let url = `${environment.apiServer}/administrator/classroom/${classroomId}`;
        return httpClient.state.delete<SuccessResponse>(url);
    }
}

class ClassroomLoadersClass {
    readonly Admin = new AdminClassroomLoaders();
    readonly Student = new StudentClassroomLoaders();
    readonly Teacher = new TeacherClassroomLoaders();
}

export const ClassroomLoaders = new ClassroomLoadersClass();
